import {
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Grid,
} from "@mui/material";
import { useEffect, useState, useMemo, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FlexBetween from "../../../components/FlexBetween";
import {
  executeResearchQueryReRun,
  backgroundExecuteResearchQueryReRun,
} from "../../../services/researchQueryExecutionService";
import ResultStorageParameters from "../../../components/Rules/ResultStorageParameters";
import RunParameters from "../../../components/Rules/RunParameters";
import ScoringConfigOverrideAccordion from "../../../components/Scoring/ScoringConfigOverrideAccordion";
import { getGenericRQScore } from "../../../services/scoringService";
import { getDefaultRunParameters } from "../../../config/runParametersConfig";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useToast } from "../../../services/utils";
import Loader from "../../../components/Molecules/Loader/Loader";
import ViewPayloadDialog from "../../../components/Dialogs/ViewPayloadDialog";
import { defaultResultStorageParameters } from "../../../services/models/execution.model";
import { IResultStorageParameters } from "../../../types/Execution";
import { executionNameSchema } from "../../../schemas";
import ExecutionName from "../../../components/Molecules/Rule/ExecutionName";
import { useBreadCrumbContext } from "../../../contexts/BreadCrumbContext";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import { useSystemContext } from "../../../contexts/TagAndSystemProvider";
import useFetchAllSystem from "../../../hooks/useFetchAllSystem";
import GenericResearchQuery from "../GenericResearchQuery";
import InlineVariables from "../../Resource/InlineVariables";
import useFetchResearchQueryRerunData from "../../../hooks/useFetchResearchQueryRerunData";
import useFetchFileProcessing from "../../../hooks/useFetchFileProcessing";
import React from "react";
import useFetchAllConnectionKey from "../../../hooks/useFetchAllConnectionKey";
import useFetchResourceColumnsByMultipleIds from "../../../hooks/useFetchResourceColumnsByMultipleIds";
import ReRunRuleExecutionTab from "../../../components/Rules/ReRun/ReRunRuleExecutionTab";
import { IconBtnEditBase, IconViewSvgWhite } from "../../../common/utils/icons";
import { processReRunResearchQueryExecutionData } from "../../../services/utils/processReRunResearchQueryExecutionData";
import { useAuth } from "../../../contexts/AuthContext";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import useFetchAllConnectionKeyWithoutId from "../../../hooks/useFetchAllConnectionKeyWithoutId";
import { validateEngineRequest } from "../../../services/resourcesService";

export default function ReRunResearchQueryExecution() {
  const { showToast } = useToast();
  const { id: currentResearchQueryId } = useParams();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const {
    setGlobalVariables,
    tempGlobalVariables,
    setTempGlobalVariables,
    isResourceEdit,
    fileProcessingData,
    setFileProcessingData,
    setFetchedAllConnectionKeys,
    setResourceColumnsData,
    linkedServicesData,
    viewInlineVariables,
    setViewInlineVariables,
  } = useRuleResourceContext();

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isBackdropLoading, setIsBackdropLoading] = useState(false);

  // Data states
  const [researchQueryDetailData, setResearchQueryDetailData] = useState<any>(
    {},
  );

  const [resourcesData, setResourcesData] = useState<any[]>([]);

  // REFRESH FIX: Track if initial data has been loaded to prevent overwriting saved changes
  const hasLoadedInitialData = useRef(false);
  const [isEditResearchQuery, setIsEditResearchQuery] = useState(false);

  // Parameters states
  const [resultStorageParameters, setResultStorageParameters] =
    useState<IResultStorageParameters>(defaultResultStorageParameters);
  const [runParameters, setRunParameters] = useState<any>(
    getDefaultRunParameters("researchQuery"),
  );
  const [scoreConfigOverride, setScoreConfigOverride] = useState<Record<
    string,
    any
  > | null>(null);
  const [priorScoreConfig, setPriorScoreConfig] = useState<Record<
    string,
    any
  > | null>(null);

  // Variables and execution states
  // const [inlineVariables, setInlineVariables] = useState<any>({});
  const [initialInlineVariables, setInitialInlineVariables] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  // const [originalInlineVariables, setOriginalInlineVariables] = useState<any>(
  //   {}
  // );
  const [executionName, setExecutionName] = useState("");
  const [executionMode, setExecutionMode] = useState(true);
  const [errors, setErrors] = useState<{ [key: string]: any }>({
    execution_name: "",
  });

  // Dialog state variables for View Payload functionality
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [
    reRunResearchQueryExecutionPayload,
    setReRunResearchQueryExecutionPayload,
  ] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);

  // Accordion and edit states
  const [expandedAccordion, setExpandedAccordion] = useState<any>("");
  const [expandedResourceAccordion, setExpandedResourceAccordion] =
    useState<any>("");
  const [isEditVariables, setIsEditVariables] = useState(false);
  const [isEditRunParameters, setIsEditRunParameters] = useState(false);
  const [isEditResultStorageParameters, setIsEditResultStorageParameters] =
    useState(false);

  // Re-run data state
  const [isReRunData, setIsReRunData] = useState<any>({
    executionID: null,
    isReRun: false,
    runId: null,
    runName: null,
    executionSubGroupId: null,
    executionSubGroupCode: null,
  });

  // Additional states for resource management
  // const [viewInlineVariables, setViewInlineVariables] = useState<any>({});
  const [fileProcessing] = useFetchFileProcessing({ setIsLoading });

  // Ref to maintain stable array references and prevent infinite loops
  const previousConnectionKeyIdsRef = useRef<number[]>([]);

  // OPTIMIZATION: Extract required connection key IDs from all resource data
  // This replaces the inefficient useFetchConnectionKeysAll call with targeted fetching
  const requiredConnectionKeyIds = useMemo(() => {
    if (
      !resourcesData ||
      !Array.isArray(resourcesData) ||
      resourcesData.length === 0
    ) {
      return previousConnectionKeyIdsRef.current;
    }

    const allConnectionKeyIds = new Set<number>();

    // DIRECT EXTRACTION for ReRun Research Query Execution data structure
    resourcesData.forEach((resource: any, index: number) => {
      // RESEARCH QUERY EXECUTION SPECIFIC: Extract from additional_properties.resource_definition
      const resourceDefinition =
        resource?.additional_properties?.resource_definition;

      if (resourceDefinition) {
        // Main connection key in resource_definition
        if (resourceDefinition.connection_key) {
          allConnectionKeyIds.add(resourceDefinition.connection_key);
        }

        // API definition connection key
        if (resourceDefinition.api_definition?.connection_key) {
          allConnectionKeyIds.add(
            resourceDefinition.api_definition.connection_key,
          );
        }

        // SQL definition connection key
        if (resourceDefinition.sql_definition?.connection_key) {
          allConnectionKeyIds.add(
            resourceDefinition.sql_definition.connection_key,
          );
        }

        // Blob definition connection key
        if (resourceDefinition.blob_definition?.connection_key) {
          allConnectionKeyIds.add(
            resourceDefinition.blob_definition.connection_key,
          );
        }

        // SFTP definition connection key
        if (resourceDefinition.sftp_definition?.connection_key) {
          allConnectionKeyIds.add(
            resourceDefinition.sftp_definition.connection_key,
          );
        }

        // Local definition connection key
        if (resourceDefinition.local_definition?.connection_key) {
          allConnectionKeyIds.add(
            resourceDefinition.local_definition.connection_key,
          );
        }
      }

      // LEGACY SUPPORT: Also check the old structure for backward compatibility
      if (resource.connection_key) {
        allConnectionKeyIds.add(resource.connection_key);
      }

      // Aggregation base resource connection key (CRITICAL: This was missing!)
      if (resource.aggregation_base_resource_data?.connection_key) {
        allConnectionKeyIds.add(
          resource.aggregation_base_resource_data.connection_key,
        );
      }

      // Additional base resource connection keys
      if (resource.additional_base_resource_data?.length > 0) {
        resource.additional_base_resource_data.forEach(
          (additionalResource: any) => {
            if (additionalResource?.connection_key) {
              allConnectionKeyIds.add(additionalResource.connection_key);
            }
          },
        );
      }

      // RERUN EXECUTION FIX: Extract connection keys from linked_service_id
      if (resource.linked_service_id && linkedServicesData) {
        const linkedService = linkedServicesData.find(
          (ls: any) => ls.id === resource.linked_service_id,
        );

        // Get connection key ID from linked service connection_details
        if (linkedService?.connection_details?.connection_keys?.length > 0) {
          linkedService.connection_details.connection_keys.forEach(
            (ckId: any) => {
              allConnectionKeyIds.add(ckId);
            },
          );
        }

        if (
          linkedService?.connection_details?.connection_keys_details?.length > 0
        ) {
          linkedService.connection_details.connection_keys_details.forEach(
            (ckDetail: any) => {
              if (ckDetail.connection_key_id) {
                allConnectionKeyIds.add(ckDetail.connection_key_id);
              }
            },
          );
        }
      }
    });

    // Also extract from research query detail data if available
    if (researchQueryDetailData?.resources) {
      researchQueryDetailData.resources.forEach((resource: any) => {
        // Main resource connection key
        if (resource.connection_key) {
          allConnectionKeyIds.add(resource.connection_key);
        }

        // Aggregation base resource connection key
        if (resource.aggregation_base_resource_data?.connection_key) {
          allConnectionKeyIds.add(
            resource.aggregation_base_resource_data.connection_key,
          );
        }

        // Additional base resource connection keys
        if (resource.additional_base_resource_data?.length > 0) {
          resource.additional_base_resource_data.forEach(
            (additionalResource: any) => {
              if (additionalResource?.connection_key) {
                allConnectionKeyIds.add(additionalResource.connection_key);
              }
            },
          );
        }
      });
    }

    // Convert to array and filter out null/undefined values
    const finalConnectionKeyIds = Array.from(allConnectionKeyIds).filter(
      (id) => id != null && id > 0,
    );

    // CRITICAL FIX: Only return new array if IDs have actually changed
    const sortedIds = Array.from(new Set(finalConnectionKeyIds)).sort(
      (a, b) => a - b,
    );
    const previousIds = previousConnectionKeyIdsRef.current;

    // Compare arrays to prevent infinite loops
    const idsChanged =
      sortedIds.length !== previousIds.length ||
      sortedIds.some((id, index) => id !== previousIds[index]);

    if (idsChanged) {
      previousConnectionKeyIdsRef.current = sortedIds;
      return sortedIds;
    } else {
      return previousConnectionKeyIdsRef.current;
    }
  }, [resourcesData, researchQueryDetailData, linkedServicesData]);

  // Fetch only the specific connection keys needed (optimized API call)
  const [fetchedConnectionKeys] = useFetchAllConnectionKeyWithoutId({
    setIsLoading,
  });
  useEffect(() => {
    if (fetchedConnectionKeys) {
      setFetchedAllConnectionKeys(fetchedConnectionKeys);
    }
  }, [fetchedConnectionKeys, setFetchedAllConnectionKeys]);

  // Fetch re-run data from API
  const executionId = new URLSearchParams(location.search).get("execution-id");
  const [rerunData] = useFetchResearchQueryRerunData({
    executionId:
      executionId && executionId !== "null" ? Number(executionId) : null,
    setIsLoading,
  });

  // RESULT STORAGE PARAMETERS FIX: Extract output storage connection key separately
  const outputStorageConnectionKeyId = useMemo(() => {
    return (
      rerunData?.output_storage_params?.output_storage_connection_key || null
    );
  }, [rerunData]);

  // Memoize the array to avoid ref changes causing repeated fetches
  const outputStorageConnectionKeyIds = useMemo(
    () => (outputStorageConnectionKeyId ? [outputStorageConnectionKeyId] : []),
    [outputStorageConnectionKeyId],
  );
  // Fetch output storage connection key separately if needed
  const [outputStorageConnectionKey] = useFetchAllConnectionKey({
    connectionKeyIds: outputStorageConnectionKeyIds,
    setIsLoading,
  });

  // Add output storage connection key to context when fetched
  useEffect(() => {
    if (outputStorageConnectionKey && outputStorageConnectionKey.length > 0) {
      // Add to context without affecting the main connection keys fetch
      setFetchedAllConnectionKeys((prevKeys: any[]) => {
        const allKeys = [...(prevKeys || []), ...outputStorageConnectionKey];
        // Remove duplicates based on ID
        const uniqueKeys = allKeys.filter(
          (key, index, self) =>
            index === self.findIndex((k) => k.id === key.id),
        );
        return uniqueKeys;
      });
    }
  }, [
    outputStorageConnectionKey,
    outputStorageConnectionKeyId,
    setFetchedAllConnectionKeys,
  ]);

  // Merge resource connection keys and output storage connection key for display components
  const combinedConnectionKeys = useMemo(() => {
    const listA = Array.isArray(fetchedConnectionKeys)
      ? fetchedConnectionKeys
      : [];
    const listB = Array.isArray(outputStorageConnectionKey)
      ? outputStorageConnectionKey
      : [];
    const all = [...listA, ...listB];
    // de-duplicate by id
    const unique = all.filter(
      (k: any, idx: number, self: any[]) =>
        k && k.id != null && idx === self.findIndex((s: any) => s.id === k.id),
    );
    return unique;
  }, [fetchedConnectionKeys, outputStorageConnectionKey]);

  // Get the setter function from context (following linkedServicesData pattern)
  const { setAllSystemData } = useSystemContext();
  // Fetch system data directly in this component (following linkedServicesData pattern)
  const [systemData] = useFetchAllSystem({
    setIsLoading,
  });
  // Update context state when system data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (systemData) {
      setAllSystemData(systemData);
    }
  }, [systemData, setAllSystemData]);

  // Extract resource column IDs from all resources and their additional resources
  // This enables column merging in LinkedServices component for ReRunResearchQueryExecution workflow
  const resourceColumnIds = useMemo(() => {
    const colIds: number[] = [];
    const resourcesData = rerunData?.resource_data;

    if (!resourcesData || !Array.isArray(resourcesData)) {
      return colIds;
    }

    // Iterate through all main resources
    resourcesData.forEach((resource: any) => {
      // Add main resource column ID
      const mainColId = resource?.resource_column_details_id;
      if (mainColId && !colIds.includes(mainColId)) {
        colIds.push(mainColId);
      }

      // Add additional base resource column IDs (ReRun uses additional_base_resource_data)
      const additionalResources = resource?.additional_base_resource_data;
      if (additionalResources && Array.isArray(additionalResources)) {
        additionalResources.forEach((addRes: any) => {
          const addColId = addRes?.resource_column_details_id;
          if (addColId && !colIds.includes(addColId)) {
            colIds.push(addColId);
          }
        });
      }
    });

    return colIds;
  }, [rerunData]);

  // Fetch resource columns for all resources (main + additional + aggregation base)
  const [resourceColumns] = useFetchResourceColumnsByMultipleIds({
    resourceColIds: resourceColumnIds,
    setIsLoading,
  });

  // Set resource columns data in context for column merging in LinkedServices
  useEffect(() => {
    if (resourceColumns?.length > 0) {
      setResourceColumnsData(resourceColumns);
    }
  }, [resourceColumns, setResourceColumnsData]);

  useEffect(() => {
    if (fileProcessing) {
      setFileProcessingData(fileProcessing);
    }
  }, [fileProcessing, setFileProcessingData]);

  // Derive the list of query names available on the loaded research query so
  // the Scoring Config Override accordion can render a searchable Autocomplete
  // for the Severity Mappings name column (mirrors the Edit screen).
  const queryNameOptions = useMemo<string[]>(() => {
    const queries = researchQueryDetailData?.research_queries;
    if (!Array.isArray(queries)) return [];
    const names = new Set<string>();
    queries.forEach((q: any) => {
      if (q?.name && typeof q.name === "string") names.add(q.name);
    });
    return Array.from(names);
  }, [researchQueryDetailData]);

  // Handle accordion changes with edit mode validation
  const handleChangeAccordion = (panel: any) => (_: any, isExpanded: any) => {
    // Check if any accordion is in edit mode
    if (
      isEditResearchQuery ||
      isEditVariables ||
      isEditRunParameters ||
      isEditResultStorageParameters ||
      isResourceEdit !== ""
    ) {
      showToast("Please save changes first!!", "warning");
      return;
    }
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleChangeResourceAccordion =
    (panel: string) => (event: any, isExpanded: any) => {
      if (
        isEditResearchQuery ||
        isEditVariables ||
        isEditRunParameters ||
        isEditResultStorageParameters ||
        isResourceEdit !== ""
      ) {
        showToast("Please save changes first!!", "warning");
        return;
      }
      setExpandedResourceAccordion(isExpanded ? panel : null);
    };

  // Load re-run data from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reRun = params.get("re-run");
    const executionId = params.get("execution-id");
    const runId = params.get("run-id");
    const runName = params.get("run-name");
    const executionSubGroupId = params.get("execution-sub-group-id");
    const executionSubGroupCode = params.get("execution-sub-group-code");

    if (reRun && executionId) {
      setIsReRunData({
        executionID: Number(executionId),
        isReRun: true,
        runId: runId !== "null" ? runId : null,
        runName: runName !== "null" ? runName : null,
        executionSubGroupId:
          executionSubGroupId && executionSubGroupId !== "null"
            ? executionSubGroupId
            : null,
        executionSubGroupCode:
          executionSubGroupCode && executionSubGroupCode !== "null"
            ? executionSubGroupCode
            : null,
      });
    } else {
      setIsReRunData({
        executionID: null,
        isReRun: false,
        runId: null,
        runName: null,
        executionSubGroupId: null,
        executionSubGroupCode: null,
      });
    }
  }, [location]);

  // Load actual re-run data from API
  useEffect(() => {
    // REFRESH FIX: Only load initial data once to prevent overwriting saved changes
    // When fetchedConnectionKeys changes (due to connection type change), this useEffect
    // would previously overwrite the locally updated resourcesData with stale rerunData
    if (
      rerunData &&
      isReRunData?.isReRun &&
      fetchedConnectionKeys &&
      !hasLoadedInitialData.current
    ) {
      // Set research query data
      if (rerunData.research_queries) {
        setResearchQueryDetailData({
          research_queries: rerunData.research_queries,
        });
      }

      // Set resources data with type property added once
      if (rerunData?.resource_data?.length > 0) {
        const resourcesWithType = rerunData.resource_data.map(
          (resource: any) => ({
            ...resource,
            type: resource.resource_type,
            inline_variables: rerunData.inline_variables,
          }),
        );
        setResourcesData(resourcesWithType);
      }

      // Set execution name
      if (rerunData.execution_name) {
        setExecutionName(rerunData.execution_name);
      }

      // Set inline variables
      if (rerunData.inline_variables) {
        // setInlineVariables(rerunData.inline_variables);
        setInitialInlineVariables(rerunData.inline_variables);
        setViewInlineVariables((prev: any) => ({
          ...prev,
          used_generic_research_query_variables: rerunData.inline_variables,
        }));
      }

      // Set run parameters: start with defaults, override with previous execution values
      if (rerunData.query_params) {
        const defaults = getDefaultRunParameters("researchQuery");
        setRunParameters({
          ...defaults,
          ...rerunData.query_params,
          run_instance: rerunData.run_instance || defaults.run_instance,
          execution_sub_group:
            rerunData.execution_sub_group || defaults.execution_sub_group,
        });
      }

      // Set result storage parameters
      if (rerunData.output_storage_params) {
        setResultStorageParameters({
          linked_service_id:
            rerunData.output_storage_params.output_storage_linked_service_id,
          linked_service_code:
            rerunData.output_storage_params.output_storage_linked_service_code,
          connection_key_id:
            rerunData.output_storage_params.output_storage_connection_key,
          file_path:
            rerunData.output_storage_params.output_storage_base_file_path,
        });
      }
      // Mark that initial data has been loaded
      hasLoadedInitialData.current = true;
    }
  }, [rerunData, isReRunData, fetchedConnectionKeys]);
  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: rerunData?.execution_name,
      id: isReRunData?.executionID,
    });
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [rerunData]);

  // Pre-load the GRQ scoring config snapshot from the prior execution so the
  // override accordion seeds its initial values from what was actually used
  // last time. A null result (no snapshot / 404) means the accordion falls
  // back to fetched defaults.
  useEffect(() => {
    if (!isReRunData?.executionID) return;
    let cancelled = false;
    getGenericRQScore(Number(isReRunData.executionID)).then((data) => {
      if (cancelled) return;
      const snapshot =
        data && typeof data === "object" && data.config_snapshot
          ? data.config_snapshot
          : null;
      setPriorScoreConfig(snapshot);
    });
    return () => {
      cancelled = true;
    };
  }, [isReRunData?.executionID]);

  // useEffect(() => {
  //   let resource_var = {};
  //   if (viewInlineVariables?.resource_variables?.length > 0) {
  //     viewInlineVariables?.resource_variables.map((res: any) => {
  //       resource_var = {
  //         ...res?.resource_vars,
  //       };
  //     });
  //     setInlineVariables((prev: any) => ({
  //       ...prev,
  //       ...resource_var,
  //     }));
  //   }
  // }, [viewInlineVariables]);

  // Update hasChanges when formData or fileData changes
  useEffect(() => {
    const inlineVariablesChanged =
      JSON.stringify(
        viewInlineVariables?.used_generic_research_query_variables,
      ) !== JSON.stringify(initialInlineVariables);

    setHasChanges(inlineVariablesChanged);
  }, [
    viewInlineVariables?.used_generic_research_query_variables,
    initialInlineVariables,
  ]);

  // Pure function to build re-run research query execution payload
  const buildReRunResearchQueryExecutionPayload = () => {
    const basePayload = processReRunResearchQueryExecutionData(
      researchQueryDetailData,
      resourcesData,
      resultStorageParameters,
      runParameters,
      viewInlineVariables?.used_generic_research_query_variables,
      executionName,
    );

    // Attach GRQ scoring overrides (flat shape, includes severity_mappings)
    // only when scoring is enabled and the user has modified values. The
    // outer wrapper (generic_execution_request) is added by the caller.
    if (runParameters?.generate_score && scoreConfigOverride) {
      basePayload.score_config_params = scoreConfigOverride;
    }

    return basePayload;
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: "/generic-research-queries/rerun",
        method: "POST",
        body: {
          generic_execution_request: payload,
          generic_research_query_id: currentResearchQueryId,
        },
        query_params: {},
        url_params: {},
      };
      const response = await validateEngineRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  // Handle copying payload to clipboard
  const handleCopyPayload = async (event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      const payload = buildReRunResearchQueryExecutionPayload();
      const formattedPayload = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(formattedPayload);
      showToast("JSON copied to clipboard!", "success");
    } catch (err) {
      console.error("Failed to copy JSON:", err);
      showToast("Failed to copy JSON to clipboard", "error");
    }
  };

  const handleViewResource = (domainId: any, resource: any, e: any) => {
    e.preventDefault();
    if (isResourceEdit !== "") {
      return;
    }
    navigate(`/resource/${domainId}/view/${resource}`);
  };
  const handleChangeVariable = (event: any) => {
    const { name, value } = event.target;

    setViewInlineVariables((prev: any) => ({
      ...prev,
      used_generic_research_query_variables: {
        ...prev?.used_generic_research_query_variables,
        [name]: value,
      },
    }));

    setTempGlobalVariables((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveVariables = () => {
    setIsEditVariables(false);
  };
  return (
    <>
      <Loader isLoading={isBackdropLoading} />
      <Loader isLoading={isLoading} />
      <div
        className="accordion-panel"
        style={{ marginBottom: "0", marginTop: "8px" }}
      >
        <Box>
          {/* Execution Name Section - First accordion */}
          <ExecutionName
            handleChangeAccordion={handleChangeAccordion}
            errors={errors}
            setErrors={setErrors}
            executionName={executionName}
            setExecutionName={setExecutionName}
            executionMode={executionMode}
            setExecutionMode={setExecutionMode}
            isShowExecutionMode={true}
            runParameters={runParameters}
            setRunParameters={setRunParameters}
            setIsLoading={setIsLoading}
            isReRun={true}
          />

          {/* Research Query Details Section */}
          <Accordion
            className="mt-8 heading-bold box-shadow color-palette color-palette-1"
            expanded={expandedAccordion === "research-queries"}
            onChange={handleChangeAccordion("research-queries")}
          >
            <AccordionSummary
              aria-controls="research-queries-content"
              id="research-queries-header"
              expandIcon={<ExpandMoreIcon />}
            >
              Research Queries
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              <GenericResearchQuery
                researchQuery={researchQueryDetailData}
                setResearchQuery={setResearchQueryDetailData}
                isFromExecution={true}
                isEditResearchQuery={isEditResearchQuery}
                setIsEditResearchQuery={setIsEditResearchQuery}
                allowExecutionOverride={true}
              />
            </AccordionDetails>
          </Accordion>

          {/* Resources Data Section */}
          {resourcesData?.length > 0 && (
            <Accordion
              className="mt-6 heading-bold box-shadow color-palette color-palette-2"
              expanded={expandedAccordion === "resources-data"}
              onChange={handleChangeAccordion("resources-data")}
            >
              <AccordionSummary
                aria-controls="resources-data-content"
                id="resources-data-header"
                expandIcon={<ExpandMoreIcon />}
              >
                Resource(s)
              </AccordionSummary>
              <AccordionDetails sx={{ paddingTop: "6px" }}>
                {resourcesData?.length > 0 &&
                  resourcesData.map((resource: any, index: number) => (
                    <>
                      <Accordion
                        className={`heading-bold box-shadow color-palette color-palette-2-a`}
                        expanded={
                          expandedResourceAccordion ===
                          `${resource?.resource_id}-${index}`
                        }
                        onChange={handleChangeResourceAccordion(
                          `${resource?.resource_id}-${index}`,
                        )}
                        key={index}
                      >
                        <AccordionSummary
                          aria-controls={`panel-${resource?.resource_id}-content`}
                          id={`panel-${resource?.resource_id}-header`}
                          expandIcon={<ExpandMoreIcon />}
                        >
                          <div className="custom-actions">
                            <div className="heading">
                              {resource?.resource_name}
                            </div>
                            <div className="action-btns">
                              <Tooltip
                                title="Navigate to view resource"
                                placement="top"
                              >
                                <button
                                  className="view-icon"
                                  onClick={(e) =>
                                    handleViewResource(
                                      resource?.domain_id
                                        ? resource?.domain_id
                                        : "all",
                                      resource?.resource_id,
                                      e,
                                    )
                                  }
                                >
                                  <IconViewSvgWhite />
                                </button>
                              </Tooltip>
                            </div>
                          </div>
                        </AccordionSummary>
                        <AccordionDetails sx={{ paddingTop: "16px" }}>
                          <React.Fragment key={resource?.resource_id}>
                            <ReRunRuleExecutionTab
                              resourcesData={resourcesData}
                              resourceData={resource}
                              isLoading={isBackdropLoading}
                              setResourcesData={setResourcesData}
                              setIsLoading={setIsLoading}
                              fileProcessingData={fileProcessingData}
                              fileStreamIndex={index}
                              setViewInlineVariables={setViewInlineVariables}
                              viewInlineVariables={viewInlineVariables}
                              isReRun={isReRunData?.isReRun}
                              fetchedConnectionKeys={fetchedConnectionKeys}
                              isFromQueryExecution={true}
                            />
                          </React.Fragment>
                        </AccordionDetails>
                      </Accordion>
                    </>
                  ))}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Run Parameters Section */}

          <RunParameters
            runParameters={runParameters}
            setRunParameters={setRunParameters}
            isEditResource={isEditRunParameters}
            setIsEditResource={setIsEditRunParameters}
            setIsLoading={setIsLoading}
            feature="researchQuery"
            expandedAccordion={expandedAccordion}
            handleChangeAccordion={handleChangeAccordion}
          />

          <ScoringConfigOverrideAccordion
            type="generic_rq"
            generateScore={!!runParameters?.generate_score}
            currentConfig={priorScoreConfig}
            expanded={expandedAccordion === "scoring-config-overrides"}
            onChange={handleChangeAccordion("scoring-config-overrides")}
            onOverrideChange={setScoreConfigOverride}
            disabled={isEditRunParameters}
            nameOptions={queryNameOptions}
          />

          {/* Result Storage Parameters Section */}
          <Accordion
            className="mt-8 heading-bold box-shadow color-palette color-palette-1"
            expanded={expandedAccordion === "result-storage-parameters"}
            onChange={handleChangeAccordion("result-storage-parameters")}
          >
            <AccordionSummary
              aria-controls="panel1d-content"
              id="panel1d-header"
              expandIcon={<ExpandMoreIcon />}
            >
              Result Storage Parameters
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: "16px" }}>
              <ResultStorageParameters
                resultStorageParameters={resultStorageParameters}
                setResultStorageParameters={setResultStorageParameters}
                isEditResource={isEditResultStorageParameters}
                setIsEditResource={setIsEditResultStorageParameters}
                setIsLoading={setIsLoading}
                isFromRule={false}
                isReRun={true}
                isFromQueryExecution={true}
                fetchedConnectionKeys={combinedConnectionKeys}
              />
            </AccordionDetails>
          </Accordion>

          {/* Variables Section */}
          {viewInlineVariables?.used_generic_research_query_variables &&
            Object.keys(
              viewInlineVariables?.used_generic_research_query_variables,
            ).length > 0 && (
              <Accordion
                className="mt-8 heading-bold box-shadow color-palette color-palette-1"
                expanded={expandedAccordion === "inline-variables"}
                onChange={handleChangeAccordion("inline-variables")}
              >
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  expandIcon={<ExpandMoreIcon />}
                >
                  Variables
                </AccordionSummary>
                <AccordionDetails sx={{ paddingTop: "16px" }}>
                  <div className="text-box-card box-card-variables full-radius">
                    {!isEditVariables ? (
                      <div className="inline-variables-parent">
                        <InlineVariables
                          inlineVariables={
                            viewInlineVariables?.used_generic_research_query_variables
                          }
                          handleChangeVariable={handleChangeVariable}
                          isReadOnly={true}
                        />
                      </div>
                    ) : (
                      <div className="inline-variables-parent">
                        <InlineVariables
                          inlineVariables={
                            viewInlineVariables?.used_generic_research_query_variables
                          }
                          handleChangeVariable={handleChangeVariable}
                          isReadOnly={false}
                        />
                      </div>
                    )}
                    {!isEditVariables ? (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md
                        sx={{
                          justifyContent: "flex-end",
                          display: "flex",
                          marginTop: "8px",
                        }}
                      >
                        <button
                          className="btn-nostyle icon-btn-edit"
                          onClick={() => {
                            setIsEditVariables(true);
                          }}
                        >
                          <IconBtnEditBase ariaLabel="Edit variables" />
                        </button>
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs
                        sx={{
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            columnGap: "8px",
                            flexWrap: "wrap",
                            marginTop: "8px",
                          }}
                        >
                          <Button
                            color="secondary"
                            variant="contained"
                            onClick={() => {
                              setIsEditVariables(false);
                            }}
                            className="btn-orange btn-dark"
                          >
                            Cancel
                          </Button>
                          <Button
                            color="secondary"
                            variant="contained"
                            onClick={handleSaveVariables}
                            className="btn-orange"
                            disabled={!hasChanges}
                          >
                            <SaveOutlinedIcon /> &nbsp; Save
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            )}

          {/* Re-Execute Button */}
          {!isEditRunParameters && !isEditResultStorageParameters && (
            <FlexBetween
              gap="1rem"
              sx={{ marginTop: 2, display: "flex", justifyContent: "flex-end" }}
            >
              {isAdmin && (
                <Button
                  className="btn-orange btn-dark"
                  onClick={() => {
                    const payload = buildReRunResearchQueryExecutionPayload();
                    setReRunResearchQueryExecutionPayload(payload);
                    setIsPayloadDialogOpen(true);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    padding: "6px 16px",
                    minWidth: "auto",
                  }}
                >
                  <Box sx={{ paddingRight: "12px" }}>View JSON</Box>
                  <Box
                    sx={{
                      width: "1px",
                      height: "24px",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    }}
                  />
                  <Box
                    onClick={handleCopyPayload}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: "18px" }} />
                  </Box>
                </Button>
              )}
              <Button
                type="submit"
                className="btn-orange"
                color="secondary"
                variant="contained"
                onClick={handleExecuteSubmit}
              >
                {isReRunData?.isReRun ? "Re-Execute Query" : "Execute Query"}
              </Button>
            </FlexBetween>
          )}
        </Box>
      </div>
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        payload={reRunResearchQueryExecutionPayload}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        dialogTitle="Re-Run Research Query Execution JSON"
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );

  async function handleExecuteSubmit() {
    try {
      await executionNameSchema.validate(
        { execution_name: executionName },
        {
          abortEarly: false,
        },
      );

      setIsBackdropLoading(true);

      // Create execution payload using the extracted function
      const reqBody = buildReRunResearchQueryExecutionPayload();

      const payload = {
        generic_execution_request: {
          ...reqBody,
        },
        generic_research_query_id: currentResearchQueryId,
      };

      // Execute the research query re-run based on execution mode
      if (executionMode) {
        // Background execution
        backgroundExecuteResearchQueryReRun(payload)
          .then(() => {
            showToast(
              "Your research query re-execution is in the queue. We will notify you once it's done!",
              "warn",
            );
            navigate(`/research-execution-history`);
          })
          .catch((error: any) => {
            console.error(error);
          })
          .finally(() => {
            setIsBackdropLoading(false);
          });
      } else {
        // Normal execution
        executeResearchQueryReRun(payload)
          .then((response: any) => {
            showToast("Research query re-execution successful!", "success");
            navigate(
              `/research-execution-history/${currentResearchQueryId}/${response?.id}/dashboard`,
              {
                state: {
                  isResearchQueryExecutionResponse: true,
                  rowData: response,
                },
              },
            );
          })
          .catch((error: any) => {
            console.error(error);
          })
          .finally(() => {
            setIsBackdropLoading(false);
          });
      }
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors(newErrors);
    }
  }
}
